import axios from 'axios';
import dayjs from 'dayjs';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

// Components

import BrokerAndAssisterScreenPopTable from '@components/crm/BrokerAndAssisterScreenPopTable';
import ScreenpopTable from '@components/crm/ScreenpopTable';
import DatePicker from '@lib/inputs/react-datepicker';

// ActionCreators

import {
    getApplicants,
    getIvrData,
    getScreenpopAssisterData,
    getScreenpopBrokerData,
} from '@actions/screenpop/screenpopActionCreators';

// Utils

import { _createParameterObject } from '@utils/miscellaneous/urlSearchParams';
import { screenpopRegex } from '@utils/screenpop/screenpopRegex';
import { alphaSort } from '@utils/tools/sortTools';

// Constants

import {
    CALLER_INFORMATION_MAX_LENGTH,
    CALLER_INFORMATION_MID_LENGTH,
    NEGATIVE,
    POSITIVE,
} from '@constants/common/miscellaneous';
import { configs } from '@constants/configs/configs';
import {
    ENTITY_TYPES,
    FORM_ASSISTER_CONFIG,
    FORM_BROKER_CONFIG,
    FORM_CONSUMER_CONFIG,
} from '@constants/crm/screenpopTypes';

const api = axios.create({
    headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': window.csrf_token,
        'X-UID': window.UID,
        'X-EMAIL': window.EMAIL,
        'X-Requested-With': 'XMLHttpRequest',
        csrftoken: window.csrf_token,
    },
});

const ScreenPopContainer = props => {
    const { getApplicants, getIvrData, getScreenpopBrokerData, getScreenpopAssisterData } = props;

    const doesChatLogExist = useMemo(() => window.location.href.includes('&source=chat'), [window.location.href]);

    const [ivrVariables, setIVRVariables] = useState();
    const [formData, setFormData] = useState({});
    const [filterData, setFilterData] = useState({
        primaryContact: '',
        additionalMembers: '',
        brokerAssister: '',
        authRepName: '',
    });

    const [callerInformation, setCallerInformation] = useState({});
    const [userData, setUserData] = useState([]);
    //useState related to API calls
    const [confirmedCaller, setConfirmedCaller] = useState({});
    const [authenticatedCallerGuid, setAuthenticatedCallerGuid] = useState(null);
    const [retrieveData, setRetrieveData] = useState(false);
    //filtered user data based off filterData
    const [filteredUserData, setFilteredUserData] = useState([]);
    //useState error handling
    const [sideFormErrors, setSideFormErrors] = useState({});
    const [isChatAuth, setIsChatAuth] = useState(false);

    const [entityType, setEntityType] = useState(undefined);
    const [brokerAssisterData, setBrokerAssisterData] = useState();
    const [datePickerValues, setDatePickerValues] = useState({ fromDate: '', toDate: '' });

    const [checkboxValues, setCheckboxValues] = useState([]);
    const [paginationData, setPaginationData] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [sortBy, setSortBy] = useState('ASC');
    const [columnName, setColumnName] = useState('');
    const [spAuthenticated, setSpAuthenticated] = useState(null);

    const [formConfig, setFormConfig] = useState(FORM_CONSUMER_CONFIG);
    //******************** End of useState setup ******************

    const clearErrorFields = (...args) => {
        const newErrorState = args.reduce((acc, key) => {
            return {
                ...acc,
                [key]: undefined,
            };
        }, {});

        setSideFormErrors({
            ...sideFormErrors,
            ...newErrorState,
        });
    };

    //This array will be used to create the headers for the table
    //as well as the filters on the top of the table.
    const filterTableHeadersConfig = [
        { defaultText: 'Primary Contact', isFilter: true, stateName: 'primaryContact' },
        { defaultText: 'Additional Members', isFilter: true, stateName: 'additionalMembers' },
        { defaultText: 'Broker/Assister', isFilter: true, stateName: 'brokerAssister' },
        { defaultText: 'Auth Rep Name', isFilter: true, stateName: 'authRepName' },
        { defaultText: 'Action', isFilter: false, stateName: 'action' },
    ];

    //enums for error messages
    const phoneNumberErrorMessage = 'Please enter a 10 digit phone number with no dashes';
    const firstAndLastNameErrorMessage =
        'First Name and last name must have at least 1 and 3 characters respectively or vice versa';
    // eslint-disable-next-line quotes
    const ssnErrorMessage = "Please enter the last for digits of a customer's SSN";
    const dateOfBirthErrorMessage =
        'Please enter an 8 digit date of birth in format "MM/DD/YYYY". First and last name are required when searching with Date of Birth.';
    const memberGuidErrorMessage = 'Please enter at least 3 characters of an alphanumeric Member GUID';
    const externalCaseIdErrorMessage = 'Please enter at least 3 characters of an alphanumeric External Case ID';
    const atLeastOneErrorMessage = 'Please enter at least one value to search by';

    //****Handle Change Functions*******
    const formHandleChange = (e, type, name, value) => {
        if (type === 'date') {
            setFormData({ ...formData, [name]: dayjs(value).format('MM/DD/YYYY') });
        } else if (type === 'checkbox') {
            const isChecked = e.target.checked;
            let updateValue = checkboxValues;

            if (isChecked && !updateValue.includes(value)) {
                updateValue = [...checkboxValues, value];
            } else if (!isChecked && updateValue.includes(value)) {
                updateValue = checkboxValues.filter(item => item !== value);
            }

            setCheckboxValues(updateValue);
            setFormData({ ...formData, [name]: updateValue });
        } else if (type === 'dropdown') {
            if (e.target.value !== 'Select') {
                setFormData({ ...formData, [name]: e.target.value });
            } else {
                setFormData({ ...formData, [name]: '' });
            }
        } else {
            setFormData({ ...formData, [e.target.name]: e.target.value });
        }
    };

    const tableHandleChange = e => {
        setFilterData({ ...filterData, [e.target.name]: e.target.value });
    };

    const handleCreateNewHouseHold = e => {
        e.preventDefault();
        const callInteractionId = ivrVariables ? ivrVariables.callInteractionId : null;

        if (callInteractionId) {
            window.location = configs.endpoints.ADD_CONSUMER.processReplacements({ callInteractionId });
        } else {
            window.location = configs.endpoints.ADD_CONSUMER_WITHOUT_CALL_ID;
        }
    };

    const handleReset = e => {
        e.preventDefault();
        setFormData(prev => Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: '' }), {}));
        setDatePickerValues({ fromDate: '', toDate: '' });
        setCheckboxValues([]);
        setSideFormErrors({});
        setUserData([]);
        setFilteredUserData([]);
        setBrokerAssisterData(undefined);
        setPaginationData(undefined);
        setFilterData({ primaryContact: '', additionalMembers: '', brokerAssister: '', authRepName: '' });
        setConfirmedCaller({});
        setIsChatAuth(false);
        setAuthenticatedCallerGuid(null);
    };

    const handleConfirmCaller = async (householdNumber, index, type, e) => {
        e.preventDefault();
        let body = {
            callInteractionId: ivrVariables.callInteractionId,
            cmrHouseholdId: householdNumber,
            callerType: type,
            callSessionId: ivrVariables.callSessionId,
        };

        await api.post(configs.endpoints.LOG_CALL_DATA, JSON.stringify(body));
        setConfirmedCaller({ ...confirmedCaller, [`${type}${index}`]: true });
        window.location = configs.endpoints.VIEW_MEMBER.processReplacements({ encryptedHouseholdId: householdNumber });
    };

    const handleGoToHouseHold = async (householdNumber, e) => {
        e.preventDefault();
        const callInteractionId =
            ivrVariables && ivrVariables.callInteractionId ? ivrVariables.callInteractionId : null;
        const body = {
            callInteractionId: callInteractionId,
            cmrHouseholdId: householdNumber,
            callerType: ENTITY_TYPES.CONSUMER,
            isChat: doesChatLogExist,
            callSessionId: ivrVariables.callSessionId,
        };
        sessionStorage.setItem('encHouseHoldId', householdNumber);

        await api.post(configs.endpoints.LOG_CALL_DATA, JSON.stringify(body));
        window.location = configs.endpoints.VIEW_MEMBER.processReplacements({ encryptedHouseholdId: householdNumber });
    };

    const validateNames = formData => {
        if (
            screenpopRegex.minThree.test(formData.firstName) &&
            screenpopRegex.minOne.test(formData.lastName) &&
            screenpopRegex.noSpace.test(formData.lastName)
        )
            return true;
        if (
            screenpopRegex.minThree.test(formData.lastName) &&
            screenpopRegex.minOne.test(formData.firstName) &&
            screenpopRegex.noSpace.test(formData.firstName)
        )
            return true;
        return false;
    };

    //****Handle the validation of the search form*******
    //This is the form validation of the search form
    const handleSideFormValidation = e => {
        e.preventDefault();
        let isValid = true;
        let atLeastOne = false;
        let errors = {};

        const consumerFormValidation = () => {
            if (formData.phoneNumber) {
                if (
                    screenpopRegex.phoneRegex.test(formData.phoneNumber) &&
                    screenpopRegex.noSpace.test(formData.phoneNumber)
                ) {
                    atLeastOne = true;
                    clearErrorFields('phoneNumber');
                } else {
                    isValid = false;
                    errors.phoneNumber = phoneNumberErrorMessage;
                    atLeastOne = true;
                }
            }
            if (formData.firstName || formData.lastName) {
                isValid = validateNames(formData);

                if (!isValid) {
                    errors.firstName = firstAndLastNameErrorMessage;
                    errors.lastName = firstAndLastNameErrorMessage;
                } else {
                    clearErrorFields('firstName', 'lastName');
                }
                atLeastOne = true;
            }
            if (formData.ssn) {
                if (!screenpopRegex.ssnRegex.test(formData.ssn) || !screenpopRegex.noSpace.test(formData.ssn)) {
                    isValid = false;
                    errors.ssn = ssnErrorMessage;
                } else {
                    clearErrorFields('ssn');
                }
                atLeastOne = true;
            }
            if (formData.dateOfBirth) {
                if (
                    !screenpopRegex.dateOfBirth.test(formData.dateOfBirth) ||
                    !screenpopRegex.noSpace.test(formData.dateOfBirth)
                ) {
                    isValid = false;
                    errors.dateOfBirth = dateOfBirthErrorMessage;
                } else {
                    isValid = validateNames(formData);
                    if (!isValid) {
                        errors.firstName = firstAndLastNameErrorMessage;
                        errors.lastName = firstAndLastNameErrorMessage;
                        errors.dateOfBirth = dateOfBirthErrorMessage;
                    } else {
                        clearErrorFields('firstName', 'lastName', 'dateOfBirth');
                    }
                }
                atLeastOne = true;
            }
            if (formData.memberGuid) {
                if (
                    !screenpopRegex.alphaNumericMinThree.test(formData.memberGuid) ||
                    !screenpopRegex.noSpace.test(formData.memberGuid)
                ) {
                    isValid = false;
                    errors.memberGuid = memberGuidErrorMessage;
                } else {
                    clearErrorFields('memberGuid');
                }
                atLeastOne = true;
            }
            if (formData.externalCaseId) {
                if (
                    !screenpopRegex.alphaNumericMinThree.test(formData.externalCaseId) ||
                    !screenpopRegex.noSpace.test(formData.externalCaseId)
                ) {
                    isValid = false;
                    errors.externalCaseId = externalCaseIdErrorMessage;
                } else {
                    clearErrorFields('externalCaseId');
                }
                atLeastOne = true;
            }
            if (!atLeastOne && entityType === ENTITY_TYPES.CONSUMER) {
                errors.atLeastOne = atLeastOneErrorMessage;
            }
            setSideFormErrors(errors);
            if ((atLeastOne || entityType !== ENTITY_TYPES.CONSUMER) && isValid) {
                setPageNumber(1);
                setRetrieveData(true);
            }
        };

        if (entityType === 'CONSUMER') {
            consumerFormValidation();
        } else {
            setPageNumber(1);
            setRetrieveData(true);
        }
    };

    const sortByHandler = useCallback(
        (name, sortBy) => {
            setColumnName(name);
            setSortBy(sortBy);
        },
        [sortBy, columnName],
    );

    const processBody = () => {
        let body = {
            pageNumber,
            sortBy: columnName,
            sortOrder: sortBy,
        };

        for (const [key, value] of Object.entries(formData)) {
            if (value !== '' && value !== undefined) {
                body = {
                    ...body,
                    [key]: value,
                };
            }
        }

        return body;
    };

    //******* Use Effect calls *************
    //initial load
    useEffect(() => {
        const obj = _createParameterObject('camelBack', 'callReason');

        getIvrData(obj.refId).then(res => {
            if (res) {
                setSpAuthenticated(res.sp_Authenticated);
                const setFormDataByEntityType = () => {
                    setEntityType(res.EntityType || ENTITY_TYPES.CONSUMER);

                    if (res.EntityType === ENTITY_TYPES.ASSISTER) {
                        setFormConfig(FORM_ASSISTER_CONFIG);
                        setFormData({
                            ...formData,
                            contactNumber: res.contactNumber || res.ani || '',
                            firstName: res.firstName || '',
                            lastName: res.lastName || '',
                            entityName: res.entityName || '',
                            status: res.status || '',
                            fromDate: res.fromDate || '',
                            toDate: res.toDate || '',
                            receivedPayments: res.receivedPayments || '',
                            organizationType: res.organizationType || '',
                        });
                    }

                    if (res.EntityType === ENTITY_TYPES.BROKER) {
                        setFormConfig(FORM_BROKER_CONFIG);
                        setFormData({
                            ...formData,
                            firstName: res.firstName || '',
                            lastName: res.lastName || '',
                            contactNumber: res.contactNumber || res.ani || '',
                            licenseNumber: res.licenseNumber || '',
                            certificationStatus: res.certificationStatus || '',
                        });
                    }

                    if (
                        res.EntityType === ENTITY_TYPES.CONSUMER ||
                        res.EntityType === null ||
                        res.EntityType === undefined
                    ) {
                        setFormConfig(FORM_CONSUMER_CONFIG);
                        setFormData({
                            ...formData,
                            phoneNumber: res.ani || '',
                            firstName: res.firstName || '',
                            lastName: res.lastName || '',
                            ssn: res.ssn || '',
                            dateOfBirth: res.dateOfBirth || '',
                            memberGuid: res.guid || '',
                            externalCaseId: res.externalCaseId || '',
                        });
                    }
                };

                setFormDataByEntityType();
                setAuthenticatedCallerGuid(res.guid || null);
            } else {
                setEntityType(ENTITY_TYPES.CONSUMER);
                setFormData({
                    ...formData,
                });

                setAuthenticatedCallerGuid(null);
            }

            setIVRVariables(res);

            if (res) {
                const spKeys = Object.keys(res)
                    .filter(k => k.startsWith('sp_'))
                    .sort(alphaSort);

                setCallerInformation(
                    spKeys.reduce((acc, k) => {
                        return {
                            ...acc,
                            [k.slice(3)]: res[k],
                        };
                    }, {}),
                );
            }
            setRetrieveData(true);
        });
    }, []);

    //useEffect related to fetching user data
    useEffect(() => {
        if (retrieveData) {
            const getConsumerData = () => {
                let body = {
                    firstName: formData.firstName || null,
                    lastName: formData.lastName || null,
                    phoneNumber: formData.phoneNumber || null,
                    last4ssn: formData.ssn || null,
                    dob: formData.dateOfBirth || null,
                    externalCaseId: formData.externalCaseId || null,
                    applicantGuid: formData.memberGuid || null,
                };

                const isExternalCaseIDOnly =
                    formData.firstName !== null &&
                    formData.lastName !== null &&
                    formData.phoneNumber !== null &&
                    formData.ssn !== null &&
                    formData.dateOfBirth !== null &&
                    formData.memberGuid !== null &&
                    formData.externalCaseId;
                getApplicants(body).then(res => {
                    if (isExternalCaseIDOnly && Array.isArray(res)) {
                        const externalCaseId = res[0]['externalCaseId'];
                        const primaryContact = res[0]['primaryContact'];

                        if (primaryContact) {
                            setFormData({
                                ...formData,
                                phoneNumber: primaryContact.phone || '',
                                firstName: primaryContact.firstName || '',
                                lastName: primaryContact.lastName || '',
                                ssn: primaryContact.last4Ssn || '',
                                dateOfBirth: primaryContact.dob || '',
                                memberGuid: primaryContact.applicantGuid || '',
                                externalCaseId: externalCaseId || '',
                            });

                            const isAuth =
                                primaryContact.dob &&
                                primaryContact.firstName &&
                                primaryContact.lastName &&
                                primaryContact.dob &&
                                externalCaseId;
                            setIsChatAuth(isAuth);
                        }
                    }

                    Array.isArray(res) && setUserData(res);
                });
            };

            if (entityType === ENTITY_TYPES.CONSUMER) {
                getConsumerData();
            }

            if (entityType === ENTITY_TYPES.BROKER) {
                const body = processBody();

                getScreenpopBrokerData({ body }).then(data => {
                    if (data) {
                        const { records, pageNumber } = data;

                        const currentPageNumber = parseInt(pageNumber);
                        const totalPages = Math.floor(records / data.content.length);

                        const processData = data.content.map(user => {
                            return {
                                ...user,
                                agentName: `${user.firstName} ${user.lastName}`,
                                url: `/hix/admin/broker/viewcertificationinformation/${user.encryptedBrokerId}`,
                                iconUrl: `/hix/admin/broker/editcertificationstatus/${user.encryptedBrokerId}`,
                            };
                        });
                        setBrokerAssisterData(processData);
                        setPaginationData({
                            pageNumber: currentPageNumber - 1,
                            totalPages,
                            first: currentPageNumber === 1,
                            last: currentPageNumber == totalPages,
                            changePage: value => setPageNumber(value + 1),
                        });
                    }
                });
            }

            if (entityType === ENTITY_TYPES.ASSISTER) {
                const body = processBody();

                getScreenpopAssisterData({ body }).then(data => {
                    if (data) {
                        const { assisters, pageNumber, recordCount } = data;

                        const currentPageNumber = parseInt(pageNumber);
                        const totalPages = Math.floor(recordCount / assisters.length);

                        setPaginationData({
                            pageNumber: currentPageNumber - 1,
                            totalPages,
                            first: currentPageNumber === 1,
                            last: currentPageNumber == totalPages,
                            changePage: value => setPageNumber(value + 1),
                        });

                        const processData = assisters.map(user => {
                            return {
                                ...user,
                                name: `${user.firstName} ${user.lastName}`,
                                url: `/hix/entity/assisteradmin/viewassisterinformation?assisterId=${user.encryptedAssisterId}`,
                                iconUrl: `/hix/entity/assisteradmin/certificationstatus?assisterId=${user.encryptedAssisterId}`,
                            };
                        });
                        setBrokerAssisterData(processData);
                    }
                });
            }

            setRetrieveData(false);
        }
    }, [retrieveData]);

    useEffect(() => {
        setRetrieveData(true);
    }, [pageNumber, sortBy, columnName]);

    //****Filtering in Table*******
    //This useEffect handles which data is displayed in the table
    useEffect(() => {
        const fData = userData.filter(obj => {
            let pContact, aMembers, bA, aRep;
            let PrimaryName = `${obj.primaryContact.firstName} ${obj.primaryContact.lastName}`;

            //****Primary Contact search*******
            pContact = PrimaryName.toLowerCase().includes(filterData.primaryContact.toLowerCase());

            //****Applicant search*******
            //Check if we have any applicant data to search with
            if (filterData.additionalMembers) {
                //Checking if the user object has any applicants
                if (obj.applicants.length) {
                    //This some statement is traversing through the applicants and will return true if we have any matches
                    aMembers = obj.applicants.some(applicants => {
                        let applicantName = `${applicants.firstName} ${applicants.lastName}`;
                        return applicantName.toLowerCase().includes(filterData.additionalMembers.toLowerCase());
                    });
                } else {
                    //If we have a search term and no applicants we will return false
                    aMembers = false;
                }
            } else {
                //if we do not have a search term we will return true
                aMembers = true;
            }

            //****Broker/Assister search*******
            //Check if we have any broker/assister data to search with
            if (filterData.brokerAssister) {
                if (obj.broker) {
                    let brokerName = `${obj.broker.firstName} ${obj.broker.lastName}`;
                    bA = brokerName.toLowerCase().includes(filterData.brokerAssister.toLowerCase());
                }
                if (obj.assister) {
                    let assisterName = `${obj.assister.firstName} ${obj.assister.lastName}`;
                    bA = assisterName.toLowerCase().includes(filterData.brokerAssister.toLowerCase());
                }
            } else {
                //if we do not have any searching to do we will return true
                bA = true;
            }

            //****Auth Rep search*******
            //Check if we have any Auth Rep data to search with
            if (filterData.authRepName) {
                if (Object.keys(obj.authRep).length) {
                    let authRepName = `${obj.authRep.firstName} ${obj.authRep.lastName}`;
                    aRep = authRepName.toLowerCase().includes(filterData.authRepName.toLowerCase());
                } else {
                    aRep = false;
                }
            } else {
                aRep = true;
            }

            return pContact && aMembers && bA && aRep;
        });

        setFilteredUserData(fData);
    }, [filterData, userData]);
    //end of useEffect filtering
    //************************************************************

    //************************************************************

    const handleonDateChange = (type, name, value) => {
        const updateDate = {
            ...datePickerValues,
            [name]: value,
        };
        setDatePickerValues(updateDate);
        formHandleChange(undefined, type, name, value);
    };

    //Form located on the left of the table
    const sideForm = () => {
        return (
            <div className="grid-col-3 screenpop-form">
                <Form>
                    {formConfig.map(obj => {
                        const name = obj.stateName;
                        const type = obj.type;
                        const dropdownValues = obj.dropdownValues;

                        return (
                            <Form.Group
                                key={`form-screenpop-${obj.stateName}`}
                                controlId={`screenpop-${obj.stateName}`}
                            >
                                {type === 'header' ? (
                                    <div className="grid-row margin-top-2 margin-bottom-1">
                                        <Form.Label className={`${obj.className ? obj.className : ''}`}>
                                            <FormattedMessage
                                                id={
                                                    obj.i18n
                                                        ? `screenpop.form.${obj.i18n}`
                                                        : `screenpop.form.${obj.stateName}`
                                                }
                                            />
                                            {sideFormErrors[obj.stateName] && (
                                                <p className="create-ticket error--msg">
                                                    <FormattedMessage
                                                        id="common.non_internationalized_text"
                                                        values={{ text: sideFormErrors[obj.stateName] }}
                                                    />
                                                </p>
                                            )}
                                        </Form.Label>
                                    </div>
                                ) : (
                                    <Form.Label className={`${obj.className ? obj.className : ''}`}>
                                        <FormattedMessage
                                            id={
                                                obj.i18n
                                                    ? `screenpop.form.${obj.i18n}`
                                                    : `screenpop.form.${obj.stateName}`
                                            }
                                        />
                                        {sideFormErrors[obj.stateName] && (
                                            <p className="create-ticket error--msg">
                                                <FormattedMessage
                                                    id="common.non_internationalized_text"
                                                    values={{ text: sideFormErrors[obj.stateName] }}
                                                />
                                            </p>
                                        )}
                                    </Form.Label>
                                )}
                                {type === 'text' && (
                                    <Form.Control
                                        size="lg"
                                        type="text"
                                        name={name}
                                        maxlength={obj.maxLength ? obj.maxLength : 20}
                                        value={formData[obj.stateName]}
                                        onChange={e => formHandleChange(e)}
                                    />
                                )}
                                {type === 'number' && (
                                    <Form.Control
                                        maxLength={obj.maxLength}
                                        name={name}
                                        size="lg"
                                        type="number"
                                        value={formData[obj.stateName]}
                                        onChange={e => formHandleChange(e)}
                                    />
                                )}
                                {type === 'date' && (
                                    <div className="grid-row flex-justify-center">
                                        <DatePicker
                                            name={name}
                                            wrapperClasses={['date-picker']}
                                            popperPlacement={name == 'fromDate' ? 'top' : 'bottom'}
                                            type="text"
                                            pattern="MM/DD/YYYY"
                                            value={datePickerValues[name]}
                                            maxDate={new Date()}
                                            withMask
                                            onDateChange={value => handleonDateChange(type, name, value)}
                                        />
                                    </div>
                                )}
                                {type === 'dropdown' && (
                                    <Form.Select
                                        key={name}
                                        aria-label="select"
                                        className="form-control form-control-lg"
                                        onChange={e => formHandleChange(e, type, name)}
                                    >
                                        {dropdownValues.map(value => {
                                            return (
                                                <option key={value} value={value}>
                                                    {value}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                )}
                                {type === 'checkbox' &&
                                    obj.checkboxValues.map(value => {
                                        return (
                                            <Form.Check
                                                key={`${type}-${value}`}
                                                type={type}
                                                id={`${type}-${value}`}
                                                label={value}
                                                style={{ display: 'flex', alignItems: 'center' }} //override css
                                                className="margin-105"
                                                onChange={e => formHandleChange(e, type, name, value)}
                                            />
                                        );
                                    })}
                            </Form.Group>
                        );
                    })}
                    {sideFormErrors.atLeastOne && (
                        <p className="create-ticket error--msg">
                            <FormattedMessage
                                id="common.non_internationalized_text"
                                values={{ text: sideFormErrors.atLeastOne }}
                            />
                        </p>
                    )}
                    <div className="display-flex flex-row margin-left-9 margin-105">
                        <button
                            className="usa-button gi-button gi-button__primary"
                            type="button"
                            onClick={e => handleReset(e)}
                        >
                            <FormattedMessage id="screenpop.button.reset_all" />
                        </button>
                        <button
                            className="usa-button gi-button gi-button__primary"
                            type="button"
                            onClick={e => handleSideFormValidation(e)}
                        >
                            <FormattedMessage id="screenpop.button.go" />
                        </button>
                    </div>
                </Form>
            </div>
        );
    };
    //end of SideForm
    //************************************************************

    //************************************************************
    //Form located to filter table results
    const filterHeader = () => {
        return (
            <Form className="ticket-header-styled grid-col-9 screenpop-inline-flex" inline>
                {filterTableHeadersConfig.map((obj, index) => {
                    if (obj.isFilter) {
                        let name = obj.stateName;
                        return (
                            <Form.Group key={`filter-screenpop-${index}`}>
                                <Form.Control
                                    size="lg"
                                    type="text"
                                    name={name}
                                    placeholder={obj.defaultText}
                                    value={filterData[obj.stateName]}
                                    onChange={e => tableHandleChange(e)}
                                />
                            </Form.Group>
                        );
                    } else {
                        return null;
                    }
                })}
            </Form>
        );
    };
    //end of filterHeader
    //************************************************************

    const callerInformationGrid = () => {
        let count = 0;
        let rowCount = 0;
        let isSummaryFound = false;
        let isSentimentFound = false;

        //determine if labels is summary, if not, up count
        const summaryAndSentimentLogic = str => {
            const regexSummary = new RegExp(/^.*Summary$/, 'g');
            const regexSentiment = new RegExp(/^.*Sentiment$/, 'g');
            const regexRow = new RegExp(/^.*row/);
            if (regexSummary.test(str) && !isSummaryFound) {
                isSummaryFound = true;
                return '-summary';
            } else if (regexSentiment.test(str) && !isSentimentFound) {
                return '-sentiment';
            } else if (regexRow.test(str)) {
                rowCount++;
                return `-row-${rowCount}`;
            } else {
                count++;
                return `-${count}`;
            }
        };

        // colorCode value if value is positive, negative, true, or false
        const colorCodeValue = key => {
            let className = '';

            const isPositive =
                ((typeof callerInformation[key] === 'string' || callerInformation[key] instanceof String) &&
                    callerInformation[key].toLowerCase()) === POSITIVE;
            const isNegative =
                ((typeof callerInformation[key] === 'string' || callerInformation[key] instanceof String) &&
                    callerInformation[key].toLowerCase()) === NEGATIVE;
            const isTruthy =
                ((typeof callerInformation[key] === 'string' || callerInformation[key] instanceof String) &&
                    callerInformation[key].toLowerCase()) === 'true' || callerInformation[key] === true;
            const isFalsy =
                ((typeof callerInformation[key] === 'string' || callerInformation[key] instanceof String) &&
                    callerInformation[key].toLowerCase()) === 'false' || callerInformation[key] === false;

            if (isPositive || isTruthy) {
                className = POSITIVE;
            }
            if (isNegative || isFalsy) {
                className = NEGATIVE;
            }

            return className;
        };

        //determine size of grid based on three scenarios => if summary key exist, callerInformation greater or less CALLER_INFORMATION_MAX_LENGTH
        const gridTemplate = callerInformation => {
            if (callerInformation && !Object.keys(callerInformation).includes('Summary')) {
                return 'not-summary';
            }

            if (callerInformation && Object.keys(callerInformation).length > CALLER_INFORMATION_MAX_LENGTH) {
                return 'large';
            } else if (callerInformation && Object.keys(callerInformation).length > CALLER_INFORMATION_MID_LENGTH) {
                return 'mid';
            } else {
                return 'small';
            }
        };

        const removeRowPrefix = k => {
            if (k.startsWith('row_')) {
                return k.slice(4);
            }
            return k;
        };

        return (
            <div className={`screenpop-caller-information__${gridTemplate(callerInformation)}`}>
                {callerInformation &&
                    Object.keys(callerInformation).map(k => {
                        const label = removeRowPrefix(k);

                        return (
                            <div
                                className={`screenpop-caller-information__item${summaryAndSentimentLogic(k)}`}
                                key={`sp_${k}_${callerInformation[k]}`}
                            >
                                <div>
                                    <span>
                                        <b>{`${label}: `}</b>
                                    </span>
                                    {/Link/i.test(k) ? (
                                        <a
                                            target="_blank"
                                            href={callerInformation[k].url}
                                        >{`${callerInformation[k].text}`}</a>
                                    ) : (
                                        <span className={colorCodeValue(k)}>{`${callerInformation[k]}`}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
            </div>
        );
    };

    const getTable = () => {
        switch (entityType) {
            case ENTITY_TYPES.CONSUMER:
                return (
                    <Fragment>
                        {filterHeader()}
                        <ScreenpopTable
                            {...{
                                authenticatedCallerGuid,
                                confirmedCaller,
                                filteredUserData,
                                filterTableHeadersConfig,
                                handleConfirmCaller,
                                handleGoToHouseHold,
                                isChatAuth,
                                spAuthenticated,
                            }}
                        />
                    </Fragment>
                );
            case ENTITY_TYPES.ASSISTER:
            case ENTITY_TYPES.BROKER:
                return (
                    <BrokerAndAssisterScreenPopTable
                        entityType={entityType}
                        data={brokerAssisterData}
                        paginationData={paginationData}
                        sortByHandler={sortByHandler}
                        sortBy={sortBy === 'ASC' ? 'DESC' : 'ASC'}
                    />
                );
        }
    };

    return (
        <Fragment>
            <div className="grid-container-fluid">
                <div className="screenpop-row">{callerInformationGrid()}</div>
                <div className="screenpop-row">
                    {sideForm()}
                    <div className="grid-col-9 screenpop-container">
                        <div className="screenpop-row flex-justify-end">
                            <button
                                className="usa-button gi-button gi-button__primary grid-col-2 screenpop-container__button"
                                onClick={e => handleCreateNewHouseHold(e)}
                            >
                                <FormattedMessage id="screenpop.button.create_new_household" />
                            </button>
                        </div>
                        {getTable()}
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
//end of Screenpop Container
//************************************************************

export default connect(null, { getApplicants, getIvrData, getScreenpopBrokerData, getScreenpopAssisterData })(
    ScreenPopContainer,
);
